import { useForm, Link } from '@inertiajs/react'
import { FormEvent } from 'react'
import PlatformLayout from '@/components/PlatformLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { ArrowLeft } from 'lucide-react'

interface InitialForm {
  name: string
  description: string
  phone: string
  address: string
  city: string
  state: string
  postal_code: string
  country: string
  legal_name: string
  cnpj: string
  privacy_contact_email: string
  owner_name: string
  owner_email: string
  owner_phone: string
}

interface Props {
  school: InitialForm
}

export default function New({ school }: Props) {
  const { data, setData, post, processing, errors } = useForm<InitialForm>(school)
  const baseError = (errors as Record<string, string>).base

  const submit = (e: FormEvent) => {
    e.preventDefault()
    post('/platform/schools')
  }

  return (
    <PlatformLayout>
      <div className="p-6 max-w-3xl space-y-6">
        <div>
          <Link href="/platform/schools" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3" /> Escolas
          </Link>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Nova escola</h1>
          <p className="text-sm text-muted-foreground">Crie a escola e o usuário owner. As credenciais provisórias serão enviadas por email.</p>
        </div>

        {baseError && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm p-3 rounded-md">
            {baseError}
          </div>
        )}

        <form onSubmit={submit} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Dados da escola</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input id="name" value={data.name} onChange={(e) => setData('name', e.target.value)} required />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="description">Descrição</Label>
                  <Textarea id="description" rows={3} value={data.description} onChange={(e) => setData('description', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefone</Label>
                  <Input id="phone" value={data.phone} onChange={(e) => setData('phone', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cnpj">CNPJ</Label>
                  <Input id="cnpj" value={data.cnpj} onChange={(e) => setData('cnpj', e.target.value)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="legal_name">Razão social</Label>
                  <Input id="legal_name" value={data.legal_name} onChange={(e) => setData('legal_name', e.target.value)} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="address">Endereço</Label>
                  <Input id="address" value={data.address} onChange={(e) => setData('address', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">Cidade</Label>
                  <Input id="city" value={data.city} onChange={(e) => setData('city', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">Estado</Label>
                  <Input id="state" value={data.state} onChange={(e) => setData('state', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="postal_code">CEP</Label>
                  <Input id="postal_code" value={data.postal_code} onChange={(e) => setData('postal_code', e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">País</Label>
                  <Input id="country" value={data.country} onChange={(e) => setData('country', e.target.value)} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Owner (administrador da escola)</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="owner_name">Nome *</Label>
                  <Input id="owner_name" value={data.owner_name} onChange={(e) => setData('owner_name', e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner_email">Email *</Label>
                  <Input id="owner_email" type="email" value={data.owner_email} onChange={(e) => setData('owner_email', e.target.value)} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner_phone">Telefone *</Label>
                  <Input id="owner_phone" value={data.owner_phone} onChange={(e) => setData('owner_phone', e.target.value)} required />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                A senha provisória será gerada automaticamente e enviada para o email informado.
              </p>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button type="submit" disabled={processing}>
              {processing ? 'Salvando...' : 'Criar escola'}
            </Button>
            <Link href="/platform/schools">
              <Button type="button" variant="outline">Cancelar</Button>
            </Link>
          </div>
        </form>
      </div>
    </PlatformLayout>
  )
}
